import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import useUserStore from "../../../stores/userStore";
import useAppStore from "../../../stores/appStore";
import { SettingItem } from "@/types";
import { BlockModalWrapper } from "../../../components/block-modal-wrapper/block-modal-wrapper";
import { ButtonOutlined } from "../../../components/button-outlined/button-outlined";
import { CustomMarkdown } from "../../custom-markdown/custom-markdown";
import {
  settingBoxStyles,
  headerTextStyles,
  textFieldStyles,
  formControlStyles,
  selectStyles,
  inputLabelStyles,
  menuItemStyles,
} from "./styles";

type ExtendedSettingItem = SettingItem & {
  customValue?: string;
};

export const SettingsCustomizeBot: React.FC = () => {
  const { customization, fetchCustomization, updateCustomization } =
    useUserStore();
  const { domain, focus } = useAppStore();
  const [updatedSettings, setUpdatedSettings] = useState<ExtendedSettingItem[]>(
    []
  );
  const [initialSettings, setInitialSettings] = useState<ExtendedSettingItem[]>(
    []
  );
  const [selectWidths, setSelectWidths] = useState<{ [key: number]: number }>(
    {}
  );
  const selectRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});

  useEffect(() => {
    fetchCustomization();
  }, [fetchCustomization, domain, focus]);

  useEffect(() => {
    if (customization && customization.length > 0) {
      const initializedSettings = customization.map((setting) => {
        const variant = setting.variants.find(
          (v: any) => v.text === setting.current_value
        );
        if (variant && variant.is_custom) {
          return { ...setting, customValue: "" };
        } else if (!variant) {
          return { ...setting, customValue: setting.current_value };
        }
        return { ...setting };
      });
      setUpdatedSettings(initializedSettings);
      setInitialSettings(initializedSettings);
    }
  }, [customization]);

  const handleSelectChange = (settingIndex: number, selectedValue: string) => {
    setUpdatedSettings((prev) =>
      prev.map((setting, idx) => {
        if (idx === settingIndex) {
          const variant = setting.variants.find(
            (v: any) => v.text === selectedValue
          );
          if (variant && variant.is_custom) {
            return { ...setting, current_value: "", customValue: "" };
          }
          return {
            ...setting,
            current_value: selectedValue,
            customValue: undefined,
          };
        }
        return setting;
      })
    );
  };

  const handleCustomInputChange = (settingIndex: number, value: string) => {
    setUpdatedSettings((prev) =>
      prev.map((setting, idx) => {
        if (idx === settingIndex) {
          return { ...setting, customValue: value, current_value: value };
        }
        return setting;
      })
    );
  };

  const handleSave = async () => {
    try {
      await updateCustomization(updatedSettings);
      setInitialSettings([...updatedSettings]);
    } catch (error) {
      console.error("Error updating customization:", error);
    }
  };

  const isChanged =
    JSON.stringify(updatedSettings) !== JSON.stringify(initialSettings);

  return (
    <BlockModalWrapper lable={"Customize Bot"}>
      {updatedSettings.map((setting, index) => {
        const isOnlyCustomVariant =
          setting.variants.length === 1 && setting.variants[0].is_custom;
        const variantMatching = setting.variants.find(
          (v: any) => v.text === setting.current_value
        );
        const isCustomSelected = variantMatching
          ? variantMatching.is_custom
          : setting.customValue !== undefined;

        return (
          <Box key={index} sx={settingBoxStyles}>
            <Typography
              variant="subtitle1"
              fontWeight="bold"
              sx={headerTextStyles}
            >
              <CustomMarkdown children={setting.name} />
            </Typography>
            {isOnlyCustomVariant ? (
              <TextField
                fullWidth
                variant="outlined"
                label="Custom"
                value={setting.customValue ?? ""}
                onChange={(e) => handleCustomInputChange(index, e.target.value)}
                sx={textFieldStyles}
              />
            ) : (
              <>
                <FormControl
                  fullWidth
                  variant="outlined"
                  sx={formControlStyles}
                  ref={(el) => {
                    if (el) {
                      selectRefs.current[index] = el;
                      const width = el.offsetWidth;
                      if (selectWidths[index] !== width) {
                        setSelectWidths((prev) => ({
                          ...prev,
                          [index]: width,
                        }));
                      }
                    }
                  }}
                >
                  <InputLabel
                    id={`setting-select-label-${index}`}
                    sx={inputLabelStyles}
                  >
                    Select Option
                  </InputLabel>
                  <Select
                    labelId={`setting-select-label-${index}`}
                    value={
                      isCustomSelected
                        ? setting.variants.find((v: any) => v.is_custom)
                            ?.text || ""
                        : setting.current_value
                    }
                    label="Select Option"
                    onChange={(e) =>
                      handleSelectChange(index, e.target.value as string)
                    }
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          width: selectWidths[index]
                            ? `${selectWidths[index]}px`
                            : "auto",
                          maxWidth: selectWidths[index]
                            ? `${selectWidths[index]}px`
                            : "100%",
                          "& .MuiMenuItem-root": {
                            display: "block",
                            whiteSpace: "normal",
                            wordWrap: "break-word",
                            overflowWrap: "anywhere",
                            maxWidth: "100%",
                            boxSizing: "border-box",
                          },
                        },
                      },
                    }}
                    sx={{
                      ...selectStyles,
                      "& .MuiSelect-select": {
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      },
                    }}
                    onOpen={() => {
                      const formControl = selectRefs.current[index];
                      if (formControl) {
                        const width = formControl.offsetWidth;
                        setSelectWidths((prev) => ({
                          ...prev,
                          [index]: width,
                        }));
                      }
                    }}
                  >
                    {setting.variants.map((variant: any, vIndex: number) => (
                      <MenuItem
                        key={vIndex}
                        value={variant.text}
                        sx={menuItemStyles}
                      >
                        {variant.text}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {isCustomSelected && (
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="Custom"
                    value={setting.customValue ?? ""}
                    onChange={(e) =>
                      handleCustomInputChange(index, e.target.value)
                    }
                    sx={textFieldStyles}
                  />
                )}
              </>
            )}
          </Box>
        );
      })}
      <Box>
        <ButtonOutlined
          variant="outlined"
          onClick={handleSave}
          disabled={!isChanged}
        >
          Save
        </ButtonOutlined>
      </Box>
    </BlockModalWrapper>
  );
};

export default SettingsCustomizeBot;
